/**
 * Labels for the signup wizard's "When did you start claiming Gift Aid?"
 * question.
 *
 * The stored values (`current`, `last_year`, `earlier`, `not_claiming`)
 * are relative, so only the display text depends on today's date. It used
 * to be hardcoded in both the radio group and the review screen, which
 * meant the wizard silently offered the wrong tax years from every 6 April
 * until someone noticed — a charity signing up in September 2026 was
 * offered 2025-26 as "this tax year". Derive it instead, and keep the two
 * screens reading from one source so they can never disagree.
 *
 * Call this per render, never at module scope: a long-lived server process
 * that started before 6 April would otherwise serve last year's labels
 * until the next deploy.
 */

import {
  formatTaxYearShort,
  getPreviousTaxYears,
  getTaxYearFromDate,
} from '@/lib/utils/tax-year';

export type GiftAidStartDate = 'current' | 'last_year' | 'earlier' | 'not_claiming';

export function giftAidStartLabels(now: Date = new Date()): Record<GiftAidStartDate, string> {
  const current = formatTaxYearShort(getTaxYearFromDate(now));
  const [lastYear, earlier] = getPreviousTaxYears(now, 2).map(formatTaxYearShort);

  return {
    current: `This tax year (${current})`,
    last_year: `Last tax year (${lastYear})`,
    earlier: `${earlier} or earlier`,
    not_claiming: 'Not claiming yet',
  };
}
